import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { Button } from '@cokitchen/cokitchen-components';
import { Link, useNavigate } from 'react-router-dom';
import SortInput from 'components/base/sort-input';
import EmptyEventState from './empty-state';
import EventCard from './event-card';
import PageHeader from 'components/base/page-header';
import { apiService } from 'services/base.service';
import './styles.scss';
import PageLoader from 'components/base/page-loader';
import { ENUM_EVENT_FILTER_LIST } from 'utils/enum';
import SearchInput from 'components/base/search-input';
import { UpdateEventType } from 'components/modals/edit-event-modal';
import { getEventFilterParams } from 'utils/functions';
import { EventType } from 'models/event.model';

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventType[]>([]);
  const [filterValue, setFilterValue] = useState<string>(ENUM_EVENT_FILTER_LIST.ALL);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const { setHideSidebar, setBackLinkTitle, setIsBackLink, activeCompany } = useDashboardContext();
  useEffect(() => {
    setHideSidebar(false);
    setIsBackLink(false);
    setBackLinkTitle('Return to Events');
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);
  const options = [
    { name: ENUM_EVENT_FILTER_LIST.ALL },
    { name: ENUM_EVENT_FILTER_LIST.MOST_RECENT },
    { name: ENUM_EVENT_FILTER_LIST.ACTIVE },
    { name: ENUM_EVENT_FILTER_LIST.INACTIVE },
    { name: ENUM_EVENT_FILTER_LIST.COMPLETED },
    { name: ENUM_EVENT_FILTER_LIST.LOW_TO_HIGH_BUDGET },
    { name: ENUM_EVENT_FILTER_LIST.HIGH_TO_LOW_BUDGET }
  ];
  const companyId = activeCompany?.id;

  const getEventsList = async () => {
    if (!companyId) return;
    setLoading(true);
    const params = getEventFilterParams(filterValue, companyId);
    try {
      const res = await apiService.get('events', {
        params
      });
      setEvents(res?.data?.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventsList();
  }, [companyId, filterValue]);

  const hostEvent = () => {
    navigate('/event-details');
  };

  const updateEventDetails = (id: string, newData: UpdateEventType) => {
    setEvents(events.map((item: EventType) => (id === item.id ? { ...item, ...newData } : item)));
  };

  const handleDeleteEvent = (id: string) => {
    setEvents(events.filter((event) => event.id !== id));
  };

  const filteredEvents = events?.filter((event) => {
    return event.name?.toLowerCase().includes(value.toLowerCase());
  });

  return (
    <div>
      <PageHeader
        title='Events'
        subtitle='Have a one time event, lets take care of your guests for you'>
        <Link to='/event-details'>
          <Button small color='primary' variant='block' className='w-36'>
            New Event
          </Button>
        </Link>
      </PageHeader>

      <div className='flex justify-between mt-5 gap-4 event_header'>
        <SortInput options={options} filterValue={filterValue} setFilterValue={setFilterValue} />
        <SearchInput value={value} handleChange={(e) => setValue(e.target.value)} />
      </div>
      {loading ? (
        <PageLoader />
      ) : events?.length === 0 ? (
        <EmptyEventState hostEvent={hostEvent} />
      ) : (
        <div>
          <EventCard
            eventList={filteredEvents}
            updateEventDetails={updateEventDetails}
            handleDeleteEvent={handleDeleteEvent}
          />
        </div>
      )}
    </div>
  );
};

export default Events;
